import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { ColumnDef } from 'src/app/model/column-definition';
import { Supplier } from 'src/app/model/supplier';
import { SupplierInterface } from 'src/app/model/supplier-interface';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { TerminationService } from 'src/app/services/termination/termination.service';
import { SupplierService } from 'src/app/services/supplier/supplier.service';

/**
 * SupplierPageComponent represents the suppliers page
 */
@Component({
  selector: 'app-supplier-page',
  templateUrl: './supplier-page.component.html',
  styleUrl: './supplier-page.component.scss'
})
export class SupplierPageComponent implements AfterViewInit, OnDestroy {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of suppliers */
  @ViewChild(MatTable) table!: MatTable<Supplier>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<Supplier>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new suppliers */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "Supplier ID", field: "merchantID"},
    {header: "Status", field: "calculatedStatus"},
    {header: "Name", field: "name"},
    {header: "Email Address", field: "emailAddress"},
    {header: "Phone Number", field: "phoneNumber", format: "phone"},
    {header: "Address", field: "address"},
    {header: "Actions", field: "actions", format: "actions"},
  ]
  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the supplier page to initialize services and set a base snapshot 
   * 
   * @param _supplierService 
   */
  constructor(
    private _supplierService: SupplierService,
    private _dialogService: DialogService,
    private _router: Router,
    private _terminationService: TerminationService,
  ) {
    // Subscribe to timer changes
    this._supplierService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and supplier data source
   */
  public ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: Supplier, filter: string): boolean => {
      const str = data.toString(this.colDefs);

      // Hotfix to allow for filtering better between active/inactive
      if ("active".indexOf(filter) === 0) {
        return str.replaceAll("inactive", "").includes(filter);
      }
      return str.includes(filter);
    }

    // Get the data when it loads and update the snapshot values
    this._sub = this._supplierService.suppliers$.subscribe(
      (resp: Supplier[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._supplierService.getSuppliers());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  public ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: Supplier, property: string): string | number => {
      switch (property) {
        case "merchantID": return +item.merchantID;

        default: return (item as any)[property];
      }
      
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Edit a supplier
   */
  public editSupplier(sup: any): void {
    const supplier = sup as Supplier;

    const id = supplier.id;
    this._dialogService.openEditSupplier(supplier).subscribe(
      (updated: SupplierInterface | undefined): void => {
        if (updated == undefined) return;

        this._supplierService.updateSupplier(id, updated);

        // Check if supplier was set to inactive
        const nowInactive = updated.status === "Inactive" && sup.status === 'Active';
        if (nowInactive) {
          this._terminationService.createSupplierTermination(supplier.id);
        }
      });
  }

  /**
   * Create a new supplier
   */
  public createSupplier(): void {
    this._dialogService.openCreateSupplier().subscribe(
      (created: SupplierInterface | undefined): void => {
        if (created == undefined) return;

        this._supplierService.createSupplier(created);
      });
  }

  /**
   * Delete a supplier
   * 
   * @param supplier The supplier to delete
   */
  public deleteSupplier(sup: any): void {
    const supplier = sup as Supplier;

    this._dialogService.openDeleteSupplier(supplier).subscribe(
      (confirmation: boolean | undefined): void => {
        if (confirmation == undefined || !confirmation) return;

        this._supplierService.deleteSupplier(supplier.asInterface());
      });
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Populate the data source meticulously so it doesn't lag the supplier
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: Supplier[]): Promise<void> {
    // Don't update the table when the user is not on the page => angular will be mad :(
    // TODO: can add this clause to updating the source data. Can then add notifications to some future service from changes found here
    if (this._router.url.replace("/", "") !== AppConstants.MENU_SUPPLIERS) return;

    let source = this.source.data;
    const current = this.source.data.map((x: Supplier) => x.copyOf());

    // For each incoming supplier, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If supplier IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the suppliers are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the supplier, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new suppliers that we don't currently have
    const add = async () => {
      for (let newSupplier of incoming) {
        source.push(newSupplier);
      }
    }
    await add();

    // Remove old suppliers that have been removed
    const remove = async () => {
      for (let oldSupplier of current) {
        source = source.filter(x => x.id !== oldSupplier.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}